/**
 * Sessions that took place before Alchely, as a therapist or a centre brings
 * them in: by hand, row by row, or from a spreadsheet. Both send the same rows.
 *
 * Only what can be told from the row itself is checked here; whose patient it
 * is, which practitioner or module a name means, are the API's answer, per row.
 */

import { normalizeHeader, parseCsvDate, type CsvTable } from "../../../shared/domain/csv";
import { cell, missingColumns, type CsvColumn, type CsvImport, type CsvRowError } from "../../../shared/domain/csvImport";

export type PastSessionProgress = "finished" | "cancelled";
export type PastSessionRequester = "patient" | "therapist";
export type PastSessionPayment = "pending" | "paid";
export type PastSessionUbication = "online" | "in person";

/**
 * One session as the API takes it. Who it was with is exactly one of a patient
 * (by id or address), a couple (by id or either partner's address) or a group
 * (by id or name); the rest is optional.
 */
export interface PastSessionInput {
    /** What the session is called in the lists; untitled when absent. */
    title?: string;
    patient_id?: string;
    patient_email?: string;
    couple_patient_id?: string;
    couple_name?: string;
    couple_email?: string;
    group_patient_id?: string;
    group_name?: string;
    therapist_id?: string;
    therapist_email?: string;
    date: string;
    hour: string;
    duration: number;
    session_status: PastSessionProgress;
    cancelled_by?: PastSessionRequester;
    payment_status: PastSessionPayment;
    ubication: PastSessionUbication;
    course_module_id?: string;
    course?: string;
    module?: string;
}

export type ImportSubjectKind = "patient" | "couple" | "group";

/** Who a row can name — a patient, a couple or a group — as the host screen offers them. */
export interface ImportSubjectOption {
    kind: ImportSubjectKind;
    id: string;
    label: string;
}

/** The API's field for each kind of subject named by id. */
export const SUBJECT_ID_FIELD: Record<ImportSubjectKind, "patient_id" | "couple_patient_id" | "group_patient_id"> = {
    patient: "patient_id",
    couple: "couple_patient_id",
    group: "group_patient_id",
};

export interface ImportPractitionerOption {
    id: string;
    label: string;
}

/** A course and its modules, as a training centre's rows choose from. */
export interface ImportCourseOption {
    id: string;
    label: string;
    modules: { id: string; label: string }[];
}

/** Whose history it is: the caller's own, or a centre's. */
export type ImportTarget = { kind: "therapist" } | { kind: "centre"; centerId: string };

export interface PastSessionResult {
    index: number;
    session_id: string | null;
    error: string | null;
}

/**
 * How the status reads in a picker: a cancellation the patient asked for is its
 * own choice, as on a session's status chip.
 */
export type PastSessionStatusChoice = "finished" | "cancelled" | "cancelled:patient";
export const PAST_SESSION_STATUS_CHOICES: PastSessionStatusChoice[] = ["finished", "cancelled", "cancelled:patient"];

export function statusFields(choice: PastSessionStatusChoice): Pick<PastSessionInput, "session_status" | "cancelled_by"> {
    if (choice === "cancelled:patient") return { session_status: "cancelled", cancelled_by: "patient" };
    if (choice === "cancelled") return { session_status: "cancelled", cancelled_by: "therapist" };
    return { session_status: "finished" };
}

export const DEFAULT_DURATION = 60;
const MAX_DURATION = 600;
export const MAX_TITLE = 200;

export function isWallClock(hour: string): boolean {
    return /^([01]\d|2[0-3]):[0-5]\d$/.test(hour);
}

export function isDuration(minutes: number): boolean {
    return Number.isInteger(minutes) && minutes >= 1 && minutes <= MAX_DURATION;
}

/** "Today" as the therapist's calendar says it, to keep a past session in the past. */
export function todayIso(now: Date = new Date()): string {
    const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
    return local.toISOString().slice(0, 10);
}

// --- spreadsheet ---------------------------------------------------------

export interface PastSessionCsvOptions {
    /** A centre's file names who held each session. */
    withPractitioner: boolean;
    /** A training centre's file names the course and module. */
    withCourse: boolean;
    /** A file for one record: every row is about them, and no column says who. */
    fixedSubject?: Pick<ImportSubjectOption, "kind" | "id">;
}

// Who it was with: exactly one of the three per row, so none is required alone.
const PATIENT: CsvColumn = { key: "email_paciente", aliases: ["patient_email", "email", "correo", "email_alumno", "correo_paciente"], required: false, example: "clara@example.com" };
// The couple's name as the app shows it; an address in it is either partner's.
const COUPLE: CsvColumn = { key: "pareja", aliases: ["couple", "nombre_pareja", "couple_name", "email_pareja", "couple_email", "correo_pareja"], required: false, example: "" };
const GROUP: CsvColumn = { key: "grupo", aliases: ["group", "group_name", "nombre_grupo"], required: false, example: "" };
const SUBJECT_COLUMNS = [PATIENT, COUPLE, GROUP];
/** The columns that say who it was with: exactly one of them per row. */
export const SUBJECT_COLUMN_KEYS = SUBJECT_COLUMNS.map((column) => column.key);
const TITLE: CsvColumn = { key: "titulo", aliases: ["title", "nombre_sesion", "titulo_sesion"], required: false, example: "Mentoría Mes 1" };
const DATE: CsvColumn = { key: "fecha", aliases: ["date", "dia"], required: true, example: "2026-03-02" };
const HOUR: CsvColumn = { key: "hora", aliases: ["hour", "time", "hora_inicio"], required: true, example: "10:30" };
const DURATION: CsvColumn = { key: "duracion", aliases: ["duration", "minutos", "duracion_minutos"], required: false, example: "50" };
const STATUS: CsvColumn = { key: "estado", aliases: ["status", "session_status"], required: true, example: "finalizada" };
const PAYMENT: CsvColumn = { key: "pago", aliases: ["payment", "payment_status", "estado_pago"], required: false, example: "pagada" };
const UBICATION: CsvColumn = { key: "modalidad", aliases: ["ubication", "modality", "lugar"], required: false, example: "presencial" };
const PRACTITIONER: CsvColumn = { key: "email_terapeuta", aliases: ["therapist_email", "email_mentor", "correo_terapeuta", "mentor_email"], required: false, example: "terapeuta@centro.es" };
const COURSE: CsvColumn = { key: "curso", aliases: ["course", "nombre_interno", "curso_interno"], required: false, example: "GES-2026-FDS" };
const MODULE: CsvColumn = { key: "modulo", aliases: ["module", "fase", "modulo_nombre"], required: false, example: "Fundamentos" };

export function pastSessionColumns({ withPractitioner, withCourse, fixedSubject }: PastSessionCsvOptions): CsvColumn[] {
    return [
        ...(fixedSubject ? [] : SUBJECT_COLUMNS),
        ...(withPractitioner ? [PRACTITIONER] : []),
        TITLE,
        DATE,
        HOUR,
        DURATION,
        STATUS,
        PAYMENT,
        UBICATION,
        ...(withCourse ? [COURSE, MODULE] : []),
    ];
}

/** Words a spreadsheet may use for each status, normalised as headers are. */
const STATUS_WORDS: Record<string, PastSessionStatusChoice> = {
    finalizada: "finished",
    finalizado: "finished",
    realizada: "finished",
    hecha: "finished",
    finished: "finished",
    done: "finished",
    cancelada: "cancelled",
    cancelado: "cancelled",
    cancelled: "cancelled",
    canceled: "cancelled",
    cancelada_terapeuta: "cancelled",
    cancelada_paciente: "cancelled:patient",
    cancelada_alumno: "cancelled:patient",
    cancelada_a_peticion_del_paciente: "cancelled:patient",
    cancelled_by_patient: "cancelled:patient",
    patient_cancelled: "cancelled:patient",
};

const PAYMENT_WORDS: Record<string, PastSessionPayment> = {
    pagada: "paid",
    pagado: "paid",
    paid: "paid",
    si: "paid",
    pendiente: "pending",
    pending: "pending",
    no: "pending",
    sin_pagar: "pending",
};

const UBICATION_WORDS: Record<string, PastSessionUbication> = {
    presencial: "in person",
    in_person: "in person",
    online: "online",
    en_linea: "online",
    videollamada: "online",
};

/** "9:05" is written by hand as often as "09:05". */
function wallClock(raw: string): string | null {
    const match = /^(\d{1,2})[:.h](\d{2})$/.exec(raw.trim());
    if (!match) return null;
    const hour = `${match[1].padStart(2, "0")}:${match[2]}`;
    return isWallClock(hour) ? hour : null;
}

/** The row's subject: the record's own, or the one column of the three it fills. */
function whoItWasWith(
    values: Record<string, string>,
    named: CsvColumn[],
    fixedSubject: PastSessionCsvOptions["fixedSubject"]
): Partial<PastSessionInput> {
    if (fixedSubject) return { [SUBJECT_ID_FIELD[fixedSubject.kind]]: fixedSubject.id };
    const [subject] = named;
    const who = cell(values, subject).trim();
    if (subject === PATIENT) return { patient_email: who };
    if (subject === GROUP) return { group_name: who };
    return who.includes("@") ? { couple_email: who } : { couple_name: who };
}

export function readPastSessions(table: CsvTable, options: PastSessionCsvOptions): CsvImport<PastSessionInput> {
    const columns = pastSessionColumns(options);
    const present = new Set(table.headers);
    const namesNobody = !options.fixedSubject && !SUBJECT_COLUMNS.some((column) => [column.key, ...column.aliases].some((key) => present.has(key)));
    const missing = [...(namesNobody ? [PATIENT.key] : []), ...missingColumns(table, columns)];
    if (missing.length) return { items: [], errors: [], missingColumns: missing };

    const items: CsvImport<PastSessionInput>["items"] = [];
    const errors: CsvRowError[] = [];
    for (const { line, values } of table.records) {
        const named = options.fixedSubject ? [] : SUBJECT_COLUMNS.filter((column) => cell(values, column).trim());
        const namesNoOne = !options.fixedSubject && named.length === 0;
        const empty = namesNoOne ? PATIENT : columns.find((column) => column.required && !cell(values, column));
        if (empty) {
            errors.push({ line, code: "missing_value", column: empty.key });
            continue;
        }
        if (named.length > 1) {
            errors.push({ line, code: "several_subjects", column: PATIENT.key });
            continue;
        }
        const refuse = (column: CsvColumn, code: CsvRowError["code"] = "invalid_value") =>
            errors.push({ line, code, column: column.key });

        const date = parseCsvDate(cell(values, DATE));
        if (!date) {
            refuse(DATE, "invalid_date");
            continue;
        }
        const hour = wallClock(cell(values, HOUR));
        if (!hour) {
            refuse(HOUR);
            continue;
        }
        const rawDuration = cell(values, DURATION);
        const duration = rawDuration ? Number(rawDuration) : DEFAULT_DURATION;
        if (!isDuration(duration)) {
            refuse(DURATION);
            continue;
        }
        const status = STATUS_WORDS[normalizeHeader(cell(values, STATUS))];
        if (!status) {
            refuse(STATUS);
            continue;
        }
        const rawPayment = cell(values, PAYMENT);
        const payment = rawPayment ? PAYMENT_WORDS[normalizeHeader(rawPayment)] : "pending";
        if (!payment) {
            refuse(PAYMENT);
            continue;
        }
        const rawUbication = cell(values, UBICATION);
        const ubication = rawUbication ? UBICATION_WORDS[normalizeHeader(rawUbication)] : "in person";
        if (!ubication) {
            refuse(UBICATION);
            continue;
        }

        const value: PastSessionInput = {
            ...whoItWasWith(values, named, options.fixedSubject),
            date,
            hour,
            duration,
            ...statusFields(status),
            payment_status: payment,
            ubication,
        };
        const title = cell(values, TITLE).trim();
        if (title) value.title = title.slice(0, MAX_TITLE);
        const practitioner = options.withPractitioner ? cell(values, PRACTITIONER).trim() : "";
        if (practitioner) value.therapist_email = practitioner;
        const course = options.withCourse ? cell(values, COURSE).trim() : "";
        const module = options.withCourse ? cell(values, MODULE).trim() : "";
        if (course) value.course = course;
        if (module) value.module = module;
        items.push({ value, lines: [line] });
    }
    return { items, errors, missingColumns: [] };
}
